import { useState } from "react";
import { api, errMessage, fileAuthUrl } from "@/portal/api";
import { useAuth } from "@/portal/AuthContext";
import { Card } from "@/pages/portal/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Upload, Check, Copy, FileCheck2, Link2 } from "lucide-react";
import { toast } from "sonner";

export default function Profile() {
  const { user, refreshUser } = useAuth();
  const [info, setInfo] = useState({
    name: user.name || "", email: user.email || "", phone: user.phone || "",
    company_name: user.company_name || "", title: user.title || "",
  });
  const [savingInfo, setSavingInfo] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [savingPw, setSavingPw] = useState(false);
  const [copied, setCopied] = useState(false);

  const setI = (k, v) => setInfo((p) => ({ ...p, [k]: v }));

  const saveInfo = async (e) => {
    e.preventDefault();
    setSavingInfo(true);
    try {
      await api.put("/api/portal/me/profile", info);
      await refreshUser();
      toast.success("Profile updated");
    } catch (err) {
      toast.error(errMessage(err));
    } finally {
      setSavingInfo(false);
    }
  };

  const changePw = async (e) => {
    e.preventDefault();
    setSavingPw(true);
    try {
      await api.post("/api/auth/change-password", pw);
      setPw({ current_password: "", new_password: "" });
      toast.success("Password changed");
    } catch (err) {
      toast.error(errMessage(err));
    } finally {
      setSavingPw(false);
    }
  };

  const upload = async (endpoint, file, msg) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(endpoint, fd);
      await refreshUser();
      toast.success(msg);
    } catch (err) {
      toast.error(errMessage(err));
    }
  };

  const referralLink = user.referral_code ? `${window.location.origin}/apply/${user.referral_code}` : null;
  const copyLink = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    toast.success("Link copied");
    setTimeout(() => setCopied(false), 1800);
  };

  const isRepLike = ["rep", "manager", "admin"].includes(user.role);

  return (
    <div className="mx-auto max-w-3xl space-y-6" data-testid="profile-page">
      {/* Avatar + referral */}
      <Card className="flex flex-wrap items-center gap-5 p-5">
        <div className="relative">
          {user.profile_picture_url ? (
            <img src={fileAuthUrl(user.profile_picture_url)} alt="" className="h-20 w-20 rounded-full object-cover" />
          ) : (
            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-navy text-2xl font-semibold text-energy">
              {(user.name || "?")[0]?.toUpperCase()}
            </div>
          )}
        </div>
        <div className="flex-1">
          <h2 className="font-heading text-xl font-semibold text-navy">{user.name}</h2>
          <p className="text-sm text-slatey capitalize">{user.role} · {user.email}</p>
          <label className="mt-2 inline-flex cursor-pointer items-center gap-1.5 text-xs font-medium text-electric hover:underline" data-testid="upload-avatar-btn">
            <Upload className="h-3.5 w-3.5" /> Change photo
            <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && upload("/api/portal/me/profile-picture", e.target.files[0], "Photo updated")} />
          </label>
        </div>
      </Card>

      {referralLink && (
        <Card className="flex flex-wrap items-center justify-between gap-3 border-energy/30 bg-energy/5 p-4">
          <div className="flex items-center gap-3">
            <Link2 className="h-5 w-5 text-energy" />
            <div>
              <p className="text-sm font-semibold text-navy">Your customer application link</p>
              <p className="break-all text-xs text-slatey" data-testid="profile-referral">{referralLink}</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={copyLink} data-testid="profile-copy-referral">
            {copied ? <Check className="mr-1.5 h-4 w-4 text-energy" /> : <Copy className="mr-1.5 h-4 w-4" />}{copied ? "Copied" : "Copy"}
          </Button>
        </Card>
      )}

      {/* Personal info */}
      <Card className="p-5">
        <h3 className="mb-4 font-heading text-sm font-semibold text-navy">Personal information</h3>
        <form onSubmit={saveInfo} className="grid gap-4 sm:grid-cols-2">
          <div><Label>Name</Label><Input value={info.name} onChange={(e) => setI("name", e.target.value)} className="mt-1.5" data-testid="pf-name" /></div>
          <div><Label>Email</Label><Input type="email" value={info.email} onChange={(e) => setI("email", e.target.value)} className="mt-1.5" data-testid="pf-email" /></div>
          <div><Label>Phone</Label><Input value={info.phone} onChange={(e) => setI("phone", e.target.value)} className="mt-1.5" data-testid="pf-phone" /></div>
          <div><Label>Company</Label><Input value={info.company_name} onChange={(e) => setI("company_name", e.target.value)} className="mt-1.5" data-testid="pf-company" /></div>
          <div className="sm:col-span-2"><Label>Title</Label><Input value={info.title} onChange={(e) => setI("title", e.target.value)} className="mt-1.5" data-testid="pf-title" /></div>
          <div className="sm:col-span-2">
            <Button type="submit" disabled={savingInfo} className="bg-electric text-white hover:bg-electric-hover" data-testid="pf-save">
              {savingInfo ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
            </Button>
          </div>
        </form>
      </Card>

      {/* Password */}
      <Card className="p-5">
        <h3 className="mb-4 font-heading text-sm font-semibold text-navy">Change password</h3>
        <form onSubmit={changePw} className="grid gap-4 sm:grid-cols-2">
          <div><Label>Current password</Label><Input type="password" value={pw.current_password} onChange={(e) => setPw((p) => ({ ...p, current_password: e.target.value }))} className="mt-1.5" data-testid="pw-current" /></div>
          <div><Label>New password</Label><Input type="password" value={pw.new_password} onChange={(e) => setPw((p) => ({ ...p, new_password: e.target.value }))} className="mt-1.5" data-testid="pw-new" placeholder="min 8 characters" /></div>
          <div className="sm:col-span-2">
            <Button type="submit" disabled={savingPw || pw.new_password.length < 8} className="bg-navy text-white hover:bg-navy/90" data-testid="pw-save">
              {savingPw ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}
            </Button>
          </div>
        </form>
      </Card>

      {/* Rep documents */}
      {isRepLike && (
        <Card className="p-5">
          <h3 className="mb-4 font-heading text-sm font-semibold text-navy">Documents</h3>
          <div className="grid gap-4 sm:grid-cols-2">
            <DocUpload label="W-9" uploaded={user.has_w9} endpoint="/api/portal/me/w9" onUpload={upload} testid="w9" />
            <DocUpload label="Voided check" uploaded={user.has_voided_check} endpoint="/api/portal/me/voided-check" onUpload={upload} testid="voided-check" />
          </div>
        </Card>
      )}
    </div>
  );
}

function DocUpload({ label, uploaded, endpoint, onUpload, testid }) {
  return (
    <div className="flex items-center justify-between rounded-md border border-line p-4">
      <div className="flex items-center gap-3">
        <FileCheck2 className={uploaded ? "h-6 w-6 text-emerald-500" : "h-6 w-6 text-line"} />
        <div>
          <p className="text-sm font-medium text-navy">{label}</p>
          <p className="text-xs text-slatey">{uploaded ? "Uploaded" : "Not uploaded"}</p>
        </div>
      </div>
      <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-line bg-white px-3 py-1.5 text-xs font-medium text-navy hover:border-navy/40" data-testid={`upload-${testid}`}>
        <Upload className="h-3.5 w-3.5" /> {uploaded ? "Replace" : "Upload"}
        <input type="file" accept=".pdf,.png,.jpg,.jpeg" className="hidden" onChange={(e) => e.target.files[0] && onUpload(endpoint, e.target.files[0], `${label} uploaded`)} />
      </label>
    </div>
  );
}
